import { AnimatePresence, AnimateSharedLayout } from 'framer-motion';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { Error } from '~/Components';
import { BOX_SHADOW } from '~/Constants/CSS.constants';
import { DRAWER_WIDTH } from '~/Constants/UI.constants';
import { AcceptRejectPublicApi } from '~/Screens/AcceptRejectPublicApi/AcceptRejectPublicApi';
import Attendance from '~/Screens/Attendance/Attendance';
import { CalendarPage } from '~/Screens/Calendar/Calendar';
import { Dashboard } from '~/Screens/Dashboard/Dashboard';
import { Drawer, useDrawer } from '~/Screens/Drawer';
import { Header } from '~/Screens/Drawer/Header';
import { EventGroupList } from '~/Screens/EventGroupList';
import { EventEdit } from '~/Screens/Events/EventCreate';
import { EventTypePicker } from '~/Screens/Events/EventCreate/EventTypePicker/EventTypePicker';
import { Import } from '~/Screens/Import';
import { Integrations } from '~/Screens/Integrations/Integrations';
import { ModifyIntegration } from '~/Screens/Integrations/ThirdParty/ModifyIntegration';
import ThirdPartyIntegrations from '~/Screens/Integrations/ThirdParty/ThirdPartyIntegrations';
import { USCConnectWrapper } from '~/Screens/Integrations/UrbanSportsConnect/ContextWrapper';
import Payouts from '~/Screens/Payouts/Payouts';
import {
  LocationEdit,
  ProviderLocations,
  ProviderSettings,
  StaffEdit,
  StaffList,
} from '~/Screens/Settings';
import { UserSettings } from '~/Screens/UserSettings';
import { ActivateVideoOnDemand } from '~/Screens/VideoOnDemand/ActivateVideoOnDemand/ActivateVideoOnDemand';
import { VideoOnDemand } from '~/Screens/VideoOnDemand/VideoOnDemand';
import { VideoOnDemandEdit } from '~/Screens/VideoOnDemand/VideoOnDemandEdit';
import { MerchantBanner } from '~/ScreensOrganisation/OrganisationMerchant/HelperMerchant/MerchantBanner';
import { selectCurrentProviderError } from '~/store/provider';
import { JumpingLoader } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';
import { REQUEST_ACTIONS } from '../Constants/Actions.constants';
import { Route as NeoRoute, PRIVATE_ROUTES_WITHOUT_SIDEBAR } from '../Constants/Routes.constants';
import {
  deleteRedirectURL,
  ToastVariant,
  useLocalStorage,
  useNavigation,
  useProvider,
  useToastMessage,
  useUser,
  ExportScheduleProvider,
} from '../Hooks';
import { EventListWrapper } from '../Screens/EventList';
import AddProductContainer from '../Screens/Events/AddProduct/AddProduct.container';
import BookingContainer from '../Screens/Events/Booking/Booking.container';
import { EventCreate } from '../Screens/Events/EventCreate/EventCreate';
import { EventPage } from '../Screens/Events/EventPage';
import { ProviderSelection } from '../Screens/ProviderSelection';
import { selectErrors } from '../store/app';
import { usePrivateRoutes } from './usePrivateRoutes';
import { ExportPage } from '~/Screens/Exports';
import { ExportProvider } from '~/Hooks/useExport';

export interface RouteParams {
  domain?: string;
}

const useStyles = makeStyles((theme) => ({
  appRootWrapper: {
    margin: 0,
    boxShadow: BOX_SHADOW,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  contentWrapper: {
    marginLeft: (props: { hideDrawer: boolean }) => (props.hideDrawer ? 0 : `${DRAWER_WIDTH}px`),
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('lg')]: {
      marginLeft: 0,
    },
  },
}));

export const PrivateRoutes = ({ hideBanner = false } = {}) => {
  // hooks
  const loc = useLocation();
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { currentProvider, error } = useProvider();
  const { role } = useUser();
  const { showToast } = useToastMessage();
  const match = useRouteMatch<{ providerId: string }>({
    path: NeoRoute.PROVIDER_DETAIL,
    location: loc,
  });

  const matchAcceptReject = useRouteMatch<RouteParams>({
    path: NeoRoute.ACCEPT_REJECT_PUBLIC_API,
    location: loc,
  });

  // state
  const {
    logout,
    redirectUrl,
    providerUUId,
    deleteProviderUUId,
    providerUUIdAfterARedirectIsCleaned,
    deleteProviderUUIdAfterARedirectIsCleaned,
  } = useLocalStorage();
  const {
    isLoggedIn,
    isUserNotVerified,
    isNoProviderSelected,
    logOutURL,
    hasUserLoaded,
    userError,
    goBackToSafety,
    shouldGoToTheDashboard,
    hasRolesLoaded,
  } = usePrivateRoutes();

  // selectors
  const providerError = useSelector(selectErrors(REQUEST_ACTIONS.GET_PROVIDER));
  const providerLoadError = useSelector(selectCurrentProviderError);
  const isOnAcceptRejectPage = !!matchAcceptReject?.url;
  const { openDrawer, closeDrawer, mobileOpen } = useDrawer();
  const hideDrawer =
    (match?.url && PRIVATE_ROUTES_WITHOUT_SIDEBAR.includes(match.url)) || isOnAcceptRejectPage;
  const { classes } = useStyles({ hideDrawer });

  // check general rerouting and access verification
  useEffect(() => {
    if (!isLoggedIn || !!providerLoadError) {
      if (
        loc.pathname !== NeoRoute.LOGIN &&
        loc.pathname !== NeoRoute.LOGIN_USC &&
        loc.pathname !== NeoRoute.ONBOARDING_POSTLOGIN
      ) {
        window.location.replace(logOutURL);
        logout();
      }
      return;
    }
    if (!hasUserLoaded || !hasRolesLoaded) {
      return;
    }
    if (isUserNotVerified) {
      if (loc.pathname !== NeoRoute.ONBOARDING_VERIFICATION) {
        navigate(NeoRoute.ONBOARDING_VERIFICATION);
      }
      return;
    }
    if (redirectUrl && !providerUUIdAfterARedirectIsCleaned && providerUUId) {
      deleteProviderUUId();
      return;
    }
    if (isNoProviderSelected && loc.pathname !== NeoRoute.ONBOARDING_POSTLOGIN) {
      if (loc.pathname !== NeoRoute.PROVIDER_SELECT) {
        navigate(NeoRoute.PROVIDER_SELECT);
      }
      return;
    }
    if (shouldGoToTheDashboard) {
      goBackToSafety();
      return;
    }
    if (redirectUrl && currentProvider) {
      navigate(`/provider/${currentProvider.domain}/${redirectUrl}`);
      deleteRedirectURL();
      deleteProviderUUIdAfterARedirectIsCleaned();
      return;
    }
  }, [
    currentProvider,
    isLoggedIn,
    shouldGoToTheDashboard,
    isUserNotVerified,
    isNoProviderSelected,
    providerLoadError,
    loc.pathname,
    hasRolesLoaded,
    role,
    redirectUrl,
    providerUUIdAfterARedirectIsCleaned,
    providerUUId,
  ]);

  useEffect(() => {
    if (providerError) {
      showToast('provider_failed', t('app.default.error'), ToastVariant.ERROR, true);
    }
  }, [providerError]);

  if (!isLoggedIn || !hasUserLoaded || !hasRolesLoaded) {
    return <JumpingLoader />;
  }

  const refreshPage = () => {
    window.location.reload();
  };

  return (
    <ExportProvider>
      <ExportScheduleProvider>
        <div className={classes.appRootWrapper}>
          {!hideBanner && <MerchantBanner />}
          {!hideDrawer && <Drawer isOpen={mobileOpen} onClose={closeDrawer} />}
          <div className={classes.contentWrapper}>
            {!hideDrawer && <Header openDrawer={openDrawer} />}
            <Error error={error || userError} errorAction={refreshPage} />
            <AnimateSharedLayout type="crossfade">
              <AnimatePresence initial={false}>
                <Switch>
                  <Route
                    component={AddProductContainer}
                    exact
                    path={NeoRoute.CUSTOMER_ADD_PRODUCT}
                  />
                  <Route component={ProviderSelection} exact path={NeoRoute.PROVIDER_SELECT} />
                  <Route component={BookingContainer} exact path={NeoRoute.BOOK_CUSTOMER} />
                  <Route component={Dashboard} exact path={NeoRoute.PROVIDER_DETAIL} />
                  <Route component={EventListWrapper} exact path={NeoRoute.EVENTS} />
                  <Route component={EventPage} exact path={NeoRoute.EVENT_DETAILS} />
                  <Route component={UserSettings} exact path={NeoRoute.USER_SETTINGS} />
                  <Route component={Import} path={NeoRoute.IMPORT} />
                  <Route
                    exact
                    component={EventCreate}
                    path={[NeoRoute.EVENT_TYPE_CREATE, NeoRoute.EVENTGROUP_EDIT]}
                  />
                  <Route exact component={ExportPage} path={NeoRoute.EXPORT_PAGE} />

                  <Route component={EventTypePicker} exact path={NeoRoute.EVENT_CREATE} />
                  <Route component={EventGroupList} exact path={NeoRoute.EVENTGROUPS} />
                  <Route component={EventEdit} path={NeoRoute.EVENT_EDIT} />
                  <Route component={CalendarPage} exact path={NeoRoute.CALENDAR} />
                  <Route component={Payouts} exact path={NeoRoute.PAYOUTS} />
                  <Route component={Attendance} exact path={NeoRoute.ATTENDANCE} />
                  <Route component={ProviderSettings} exact path={NeoRoute.PROVIDER_SETTINGS} />
                  <Route component={ProviderLocations} exact path={NeoRoute.PROVIDER_LOCATIONS} />
                  <Route
                    component={LocationEdit}
                    exact
                    path={NeoRoute.PROVIDER_LOCATIONS_EDIT_BASE}
                  />
                  <Route component={LocationEdit} exact path={NeoRoute.PROVIDER_LOCATIONS_CREATE} />
                  <Route component={StaffList} exact path={NeoRoute.PROVIDER_STAFF} />
                  <Route component={StaffEdit} exact path={NeoRoute.PROVIDER_STAFF_CREATE} />
                  <Route
                    component={AcceptRejectPublicApi}
                    path={NeoRoute.ACCEPT_REJECT_PUBLIC_API}
                  />
                  <Route component={Integrations} path={NeoRoute.INTEGRATIONS} exact />
                  <Route
                    component={USCConnectWrapper}
                    path={NeoRoute.URBANSPORTSINTEGRATION}
                    exact
                  />
                  <Route
                    component={ThirdPartyIntegrations}
                    path={NeoRoute.THIRDPARTYINTEGRATION}
                    exact
                  />
                  <Route
                    component={ModifyIntegration}
                    path={NeoRoute.THIRDPARTYINTEGRATIONCREATE}
                  />
                  <Route component={VideoOnDemand} path={NeoRoute.VIDEO_ON_DEMAND} exact />
                  <Route
                    component={VideoOnDemandEdit}
                    exact
                    path={NeoRoute.VIDEO_ON_DEMAND_CREATE}
                  />
                  <Route component={VideoOnDemandEdit} exact path={NeoRoute.VIDEO_ON_DEMAND_EDIT} />
                  <Route
                    component={ActivateVideoOnDemand}
                    path={NeoRoute.VIDEO_ON_DEMAND_ACTIVATE}
                    exact
                  />
                </Switch>
              </AnimatePresence>
            </AnimateSharedLayout>
          </div>
        </div>
      </ExportScheduleProvider>
    </ExportProvider>
  );
};
export default PrivateRoutes;
